import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  ORIGINS,
  ORIGIN_FIRST,
  ORIGIN_LABEL,
  STAGE_LABEL,
  isArchivedCase,
  type DateRange,
  type SortPreset,
} from '@/domain';
import { useArchivedCases } from '@/application/usecases/useBulk';
import { useTableOrder } from '@/application/usecases/useTableOrder';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import { PAGE_SIZES, useTablePage } from '@/application/usecases/useTablePage';
import type { ArchivedCaseRow } from '@/application/ports/ArchiveRepository';
import { formatDay } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { CaseTable } from '@/ui/data/CaseTable';
import { CASE_SORT_LABEL } from '@/ui/data/casePresets';
import { FilterBar, type FilterDef } from '@/ui/data/FilterBar';
import type { Column } from '@/ui/data/ResponsiveTable';
import { SortSelect } from '@/ui/data/SortSelect';
import { useSelection } from '@/ui/data/useSelection';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { SelectionActions } from '@/features/bulk/SelectionActions';
import {
  usePermittedActions,
  type SelectionAction,
} from '@/features/bulk/permittedActions';
import styles from './ArchiveScreen.module.css';

/** What a selection of archived cases can have done to it: out again, or deleted. */
const ARCHIVED_ACTIONS: readonly SelectionAction[] = ['unarchive', 'delete'];
const rowKey = (row: ArchivedCaseRow) => row.jobOrderNo;

/**
 * The archive's Archived tab. Section 4 of the request of 24 September 2026.
 *
 * Every case archived from Orders or the production line, the most recently
 * archived first: off every list of work in hand without leaving the lab and
 * without being deleted. Each row says where its tray stopped, which is where it
 * comes back to, and who archived it and why. Filtered and ordered on the
 * server, as the other two sets are, and a selection can be taken out of the
 * archive or deleted together.
 */
export function ArchivedCases() {
  const navigate = useNavigate();

  const [q, setQ] = useState('');
  const [origin, setOrigin] = useState('all');
  const [range, setRange] = useState<DateRange>({ from: '', to: '' });

  /*
   * The most recently archived first, the order the server answers in, and
   * then the three origin sorts of section 2.
   */
  const presets = useMemo<SortPreset<ArchivedCaseRow>[]>(
    () => [
      { key: 'recentlyArchived', label: strings.archived.recentlyArchived },
      ...ORIGINS.map((key) => ({
        key: ORIGIN_FIRST[key],
        label: CASE_SORT_LABEL[ORIGIN_FIRST[key]],
        sortParam: ORIGIN_FIRST[key],
      })),
    ],
    [],
  );
  const order = useTableOrder('archive:archived', presets);

  // A page at a time, from the server, as every set of the archive is.
  const paging = useTablePage('archived');
  // The columns as this reader laid them out, kept on their account. The request of 7 October 2026, section 3.
  const layout = useTableLayout('archived');

  const filter = useMemo(
    () => ({
      set: 'archived' as const,
      q,
      origin,
      from: range.from,
      to: range.to,
      ...(order.sortParam === undefined ? {} : { sort: order.sortParam }),
      page: paging.page,
      pageSize: paging.pageSize,
    }),
    [q, origin, range, order.sortParam, paging.page, paging.pageSize],
  );

  const { data, isPending, isError } = useArchivedCases(filter);
  /*
   * An API without the Archived set ignores `set=archived` and answers with the
   * delivered cases, which production passes through unvalidated. Rows that are
   * not archived are not this tab's, and such an answer reads as nothing
   * archived rather than as delivered cases, or as a screen that throws reading
   * an archive block they do not carry.
   */
  const answered = data?.items ?? [];
  const understood = answered.every(isArchivedCase);
  const rows = understood ? answered : [];
  const total = understood ? (data?.total ?? 0) : 0;
  const filtering = q.trim() !== '' || origin !== 'all' || range.from !== '' || range.to !== '';

  // A filter or a new order makes a different list, and page three of the old
  // one is nothing in the new one.
  const { reset } = paging;
  useEffect(() => {
    reset();
  }, [reset, q, origin, range, order.presetKey, order.column]);

  const actions = usePermittedActions(ARCHIVED_ACTIONS);
  const selection = useSelection(rowKey);

  const filters: FilterDef[] = [
    {
      kind: 'search',
      label: strings.archived.search,
      placeholder: strings.archived.searchPlaceholder,
      value: q,
      onChange: setQ,
    },
    {
      kind: 'select',
      label: strings.archive.origin,
      value: origin,
      options: [
        { value: 'all', label: strings.archive.allOrigins },
        ...ORIGINS.map((key) => ({ value: key, label: ORIGIN_LABEL[key] })),
      ],
      onChange: setOrigin,
    },
    {
      kind: 'dateRange',
      label: strings.archived.archivedOn,
      from: range.from,
      to: range.to,
      onChange: setRange,
    },
  ];

  const columns: Column<ArchivedCaseRow>[] = [
    {
      key: 'product',
      label: strings.archived.colItem,
      sortable: true,
      render: (row) => row.product,
    },
    {
      key: 'origin',
      label: strings.archive.origin,
      sortable: true,
      sortValue: (row) => ORIGINS.indexOf(row.origin),
      render: (row) => ORIGIN_LABEL[row.origin],
    },
    {
      // Where the tray stopped, which is where it comes back to.
      key: 'stage',
      label: strings.archived.colStoppedAt,
      sortable: true,
      sortValue: (row) => row.stage ?? '',
      render: (row) => (row.stage === null ? '' : STAGE_LABEL[row.stage]),
    },
    {
      key: 'archivedAt',
      label: strings.archived.colArchived,
      mono: true,
      nowrap: true,
      sortable: true,
      sortValue: (row) => row.archive.archivedAt ?? '',
      // The lab's calendar day, not the UTC one, as the archive's dates are.
      render: (row) => (row.archive.archivedAt === null ? '' : formatDay(row.archive.archivedAt)),
    },
    {
      key: 'archivedBy',
      label: strings.archived.colBy,
      render: (row) => row.archive.archivedByUserName ?? '',
    },
    {
      key: 'reason',
      label: strings.archived.colReason,
      render: (row) => row.archive.reason ?? '',
    },
    // What the rows carry and the table does not show as it comes. The request of 8 October 2026 (second).
    {
      key: 'units',
      label: strings.table.columns.units,
      mono: true,
      align: 'right',
      sortable: true,
      optional: true,
      sortValue: (row) => row.units,
      render: (row) => row.units,
    },
    {
      // A date and no more: an archived case is late on nobody's bench.
      key: 'deliveryDate',
      label: strings.table.columns.due,
      mono: true,
      nowrap: true,
      sortable: true,
      optional: true,
      sortValue: (row) => row.deliveryDate,
      render: (row) => row.deliveryDate,
    },
  ];

  return (
    <>
      <FilterBar
        label={strings.archived.filtersLabel}
        filters={filters}
        sort={<SortSelect presets={presets} value={order.presetKey} onChange={order.choose} />}
      />

      {isError && <p className={styles.hint}>{strings.errors.server}</p>}

      {!isError &&
        (isPending ? (
          <SkeletonRows rows={4} label={strings.archived.loading} />
        ) : (
          <CaseTable
            customize={layout}
            columns={columns}
            rows={order.apply(rows)}
            selection={actions.length > 0 ? selection : undefined}
            sort={order.column}
            onSortChange={order.sortByColumn}
            identity={(row) => ({
              jobOrderNo: row.jobOrderNo,
              dentistName: row.dentistName,
              patientName: row.patientName,
            })}
            rowKey={rowKey}
            caption={strings.archived.caption}
            emptyHeading={filtering ? strings.table.noMatch : strings.archived.emptyHeading}
            emptyDetail={filtering ? strings.table.noMatchDetail : strings.archived.emptyDetail}
            onRowClick={(row) => void navigate(`/archive/${row.jobOrderNo}`)}
            paging={{
              page: paging.page,
              pageSize: paging.pageSize,
              total,
              sizes: PAGE_SIZES,
              onPage: paging.goTo,
              onPageSize: paging.choose,
              label: strings.table.pagesLabel,
            }}
          />
        ))}

      {/* After the table, so the bar appearing moves no row under the pointer. */}
      <SelectionActions
        count={selection.count}
        jobOrderNos={selection.rows.map((row) => row.jobOrderNo)}
        assignTarget={{ jobOrderNos: selection.rows.map((row) => row.jobOrderNo) }}
        commonStage={null}
        actions={actions}
        onClear={selection.clear}
        onSelectPage={
          rows.every((row) => selection.selected.has(row.jobOrderNo))
            ? undefined
            : () => selection.onTogglePage(rows, true)
        }
      />
    </>
  );
}
